import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLogin from './AdminLogin';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { SketchPicker } from 'react-color';
import { toast } from 'sonner';
import { 
  Calendar, Users, CheckCircle, Clock, XCircle, 
  LogOut, Search, Filter, ChevronDown, Eye, Edit2, 
  Trash2, Download, RefreshCw, UserPlus, Shield,
  Menu, X, BarChart3, Lock, Home, Mail, FileText,
  Image, Layers, Settings, MessageSquare, BookOpen,
  PlusCircle, Save, ArrowLeft, ExternalLink, Globe,
  Layout, Sliders, FileImage, Star, Upload, AlertCircle,
  FileDown, Link as LinkIcon, Copy, Play
} from 'lucide-react';
import AdminChallengeManager from './AdminChallengeManager';
import debounce from 'lodash.debounce';

const ABOUT_INPUT_CLS = "w-full px-3 py-2 bg-charcoal border border-white/10 rounded-lg text-cream text-sm focus:border-gold focus:outline-none";
const ABOUT_LABEL_CLS = "block text-gold text-xs uppercase tracking-wider mb-1";

const AboutEditorView = ({ aboutForm, setAboutForm, aboutSaving, setAboutSaving, setAboutData, uploadFile, backendUrl, getAuthHeader }) => {
  if (!aboutForm) return <div className="text-cream-400 text-center py-12">Loading about data...</div>;

  const updateField = (field, value) => setAboutForm(prev => ({ ...prev, [field]: value }));
  const updateListItem = (field, idx, value) => setAboutForm(prev => {
    const arr = [...(prev[field] || [])];
    arr[idx] = value;
    return { ...prev, [field]: arr };
  });
  const addListItem = (field) => setAboutForm(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  const removeListItem = (field, idx) => setAboutForm(prev => {
    const arr = [...(prev[field] || [])];
    arr.splice(idx, 1);
    return { ...prev, [field]: arr };
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await uploadFile(file, 'about');
    if (result?.url) updateField('image_url', result.url);
  };

  const handleSave = async () => {
    setAboutSaving(true);
    try {
      const payload = {
        image_url: aboutForm.image_url,
        tagline: aboutForm.tagline,
        tagline_ar: aboutForm.tagline_ar,
        title: aboutForm.title,
        title_ar: aboutForm.title_ar,
        tags: aboutForm.tags,
        tags_ar: aboutForm.tags_ar,
        credentials: aboutForm.credentials,
        credentials_ar: aboutForm.credentials_ar,
        bio: aboutForm.bio,
        bio_ar: aboutForm.bio_ar,
      };
      const response = await fetch(`${backendUrl}/api/admin/cms/about`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const updated = await response.json();
        setAboutData(updated);
        setAboutForm(JSON.parse(JSON.stringify(updated)));
        toast.success('About section saved successfully!');
      } else {
        let errMsg = 'Failed to save';
        try {
          const errData = await response.json();
          errMsg = errData.detail || errData.message || errMsg;
        } catch (_) {}
        console.error('About save error:', response.status, errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      console.error('About save error:', err);
      toast.error('Network error — could not save');
    } finally {
      setAboutSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Edit About Page</h3>
        <button
          onClick={handleSave}
          disabled={aboutSaving}
          className="flex items-center gap-2 px-5 py-2 bg-gold text-charcoal rounded-lg font-semibold hover:bg-gold/90 disabled:opacity-60"
        >
          <Save size={15} />
          {aboutSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-4">
        <h4 className="text-cream font-medium">Photo</h4>
        <div className="flex items-start gap-6">
          {aboutForm.image_url && (
            <img src={aboutForm.image_url} alt="About" className="w-28 h-36 object-cover rounded-xl border border-gold/20" />
          )}
          <div className="flex-1 space-y-2">
            <label className={ABOUT_LABEL_CLS}>Image URL</label>
            <input value={aboutForm.image_url || ''} onChange={e => updateField('image_url', e.target.value)} className={ABOUT_INPUT_CLS} placeholder="https://..." />
            <p className="text-cream-400/60 text-xs">Or upload a new photo:</p>
            <label className="flex items-center gap-2 px-4 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream-300 hover:text-gold cursor-pointer text-sm w-fit">
              <Upload size={14} />
              <span>Upload Photo</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </div>
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-4">
        <h4 className="text-cream font-medium">Title & Tagline</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={ABOUT_LABEL_CLS}>Tagline (EN)</label>
            <input value={aboutForm.tagline || ''} onChange={e => updateField('tagline', e.target.value)} className={ABOUT_INPUT_CLS} />
          </div>
          <div>
            <label className={ABOUT_LABEL_CLS}>Tagline (AR)</label>
            <input value={aboutForm.tagline_ar || ''} onChange={e => updateField('tagline_ar', e.target.value)} className={ABOUT_INPUT_CLS} dir="rtl" />
          </div>
          <div>
            <label className={ABOUT_LABEL_CLS}>Name / Title (EN)</label>
            <input value={aboutForm.title || ''} onChange={e => updateField('title', e.target.value)} className={ABOUT_INPUT_CLS} />
          </div>
          <div>
            <label className={ABOUT_LABEL_CLS}>Name / Title (AR)</label>
            <input value={aboutForm.title_ar || ''} onChange={e => updateField('title_ar', e.target.value)} className={ABOUT_INPUT_CLS} dir="rtl" />
          </div>
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-4">
        <h4 className="text-cream font-medium">Tags / Role Chips</h4>
        <div className="space-y-3">
          {(aboutForm.tags || []).map((tag, i) => (
            <div key={`tag-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
              <input value={tag} onChange={e => updateListItem('tags', i, e.target.value)} className={ABOUT_INPUT_CLS} placeholder={`Tag ${i + 1} (EN)`} />
              <div className="flex gap-2">
                <input value={(aboutForm.tags_ar || [])[i] || ''} onChange={e => updateListItem('tags_ar', i, e.target.value)} className={`${ABOUT_INPUT_CLS} flex-1`} placeholder={`Tag ${i + 1} (AR)`} dir="rtl" />
                <button onClick={() => { removeListItem('tags', i); removeListItem('tags_ar', i); }} className="px-2 py-2 text-red-400 hover:bg-white/5 rounded-lg"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <button onClick={() => { addListItem('tags'); addListItem('tags_ar'); }} className="flex items-center gap-2 text-gold hover:text-gold/80 text-sm">
            <PlusCircle size={14} /> Add Tag
          </button>
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-4">
        <h4 className="text-cream font-medium">Training & Credentials</h4>
        <div className="space-y-3">
          {(aboutForm.credentials || []).map((cred, i) => (
            <div key={`cred-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
              <input value={cred} onChange={e => updateListItem('credentials', i, e.target.value)} className={ABOUT_INPUT_CLS} placeholder={`Credential ${i + 1} (EN)`} />
              <div className="flex gap-2">
                <input value={(aboutForm.credentials_ar || [])[i] || ''} onChange={e => updateListItem('credentials_ar', i, e.target.value)} className={`${ABOUT_INPUT_CLS} flex-1`} placeholder={`Credential ${i + 1} (AR)`} dir="rtl" />
                <button onClick={() => { removeListItem('credentials', i); removeListItem('credentials_ar', i); }} className="px-2 py-2 text-red-400 hover:bg-white/5 rounded-lg"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <button onClick={() => { addListItem('credentials'); addListItem('credentials_ar'); }} className="flex items-center gap-2 text-gold hover:text-gold/80 text-sm">
            <PlusCircle size={14} /> Add Credential
          </button>
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-4">
        <h4 className="text-cream font-medium">Biography Paragraphs</h4>
        <p className="text-cream-400/60 text-xs">Three paragraphs are displayed: paragraph 1 (main bio), paragraph 2 (highlighted callout), paragraph 3 (supporting text).</p>
        {[0, 1, 2].map(i => (
          <div key={`bio-${i}`} className="space-y-2">
            <p className="text-gold/70 text-xs uppercase tracking-wider">Paragraph {i + 1}</p>
            <textarea
              value={(aboutForm.bio || [])[i] || ''}
              onChange={e => updateListItem('bio', i, e.target.value)}
              rows={3}
              className={`${ABOUT_INPUT_CLS} resize-none`}
              placeholder={`Paragraph ${i + 1} (EN)`}
            />
            <textarea
              value={(aboutForm.bio_ar || [])[i] || ''}
              onChange={e => updateListItem('bio_ar', i, e.target.value)}
              rows={3}
              className={`${ABOUT_INPUT_CLS} resize-none`}
              placeholder={`Paragraph ${i + 1} (AR)`}
              dir="rtl"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const GalleryView = ({ galleryItems, fetchGalleryItems, backendUrl, getAuthHeader, uploadFile }) => {
  const [uploading, setUploading] = React.useState(false);
  const [editingCaption, setEditingCaption] = React.useState({});
  const fileInputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'gallery');
        const r = await fetch(`${backendUrl}/api/admin/upload`, {
          method: 'POST',
          headers: { Authorization: getAuthHeader().Authorization },
          body: formData,
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      }
      await fetchGalleryItems();
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded to gallery`);
    } catch (err) {
      toast.error('Upload error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveCaption = async (id, caption, caption_ar) => {
    try {
      const r = await fetch(`${backendUrl}/api/admin/cms/media/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption, caption_ar }),
      });
      if (r.ok) {
        await fetchGalleryItems();
        toast.success('Caption saved');
      } else {
        toast.error('Failed to save caption');
      }
    } catch {
      toast.error('Failed to save caption');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item from the gallery?')) return;
    try {
      const r = await fetch(`${backendUrl}/api/admin/cms/media/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (r.ok) { await fetchGalleryItems(); toast.success('Deleted'); }
      else toast.error('Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  const inputCls = "w-full px-2.5 py-1.5 bg-charcoal border border-white/10 rounded-lg text-cream text-xs focus:border-gold focus:outline-none";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-cream">Gallery / Healing in Action</h3>
          <p className="text-cream-400/60 text-xs mt-1">Upload images and videos that appear in the public gallery section</p>
        </div>
        <label className={`flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium cursor-pointer hover:bg-gold/90 ${uploading ? 'opacity-60 pointer-events-none' : ''} flex-shrink-0`}>
          {uploading ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? 'Uploading...' : 'Upload Files'}
          <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" />
        </label>
      </div>

      {galleryItems.length === 0 ? (
        <div className="border-2 border-dashed border-white/10 rounded-xl p-16 text-center">
          <Image size={32} className="text-cream-400 mx-auto mb-3" />
          <p className="text-cream-400 text-sm">No gallery items yet</p>
          <p className="text-cream-500/60 text-xs mt-1">Upload images or videos to populate the public gallery</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryItems.map((item) => {
            const isVideo = item.file_type?.startsWith('video') || item.url?.match(/\.(mp4|webm|mov)$/i);
            const cap = editingCaption[item.id] ?? item.caption ?? '';
            const cap_ar = editingCaption[`${item.id}_ar`] ?? item.caption_ar ?? '';
            return (
              <div key={item.id} className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
                <div className="relative aspect-video bg-black">
                  {isVideo ? (
                    <video src={item.url} muted playsInline className="w-full h-full object-cover" />
                  ) : (
                    <img src={item.url} alt={item.alt_text || ''} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-2 left-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isVideo ? 'bg-purple-500/80 text-white' : 'bg-gold/80 text-charcoal'}`}>
                      {isVideo ? 'Video' : 'Image'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg text-white"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  <div>
                    <label className="block text-gold/70 text-xs uppercase tracking-wider mb-1">Caption (EN)</label>
                    <input
                      value={cap}
                      onChange={e => setEditingCaption(prev => ({ ...prev, [item.id]: e.target.value }))}
                      onBlur={() => saveCaption(item.id, cap, cap_ar)}
                      className={inputCls}
                      placeholder="e.g. Crystal Healing"
                    />
                  </div>
                  <div>
                    <label className="block text-gold/70 text-xs uppercase tracking-wider mb-1">Caption (AR)</label>
                    <input
                      value={cap_ar}
                      onChange={e => setEditingCaption(prev => ({ ...prev, [`${item.id}_ar`]: e.target.value }))}
                      onBlur={() => saveCaption(item.id, cap, cap_ar)}
                      className={inputCls}
                      placeholder="مثلاً: شفاء الكريستال"
                      dir="rtl"
                    />
                  </div>
                  <p className="text-cream-500/50 text-xs truncate">{item.filename}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout, getAuthHeader, isSuperAdmin, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [media, setMedia] = useState([]);
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [digitalAssets, setDigitalAssets] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [constants, setConstants] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [pagesList, setPagesList] = useState([]);
  const [programsList, setProgramsList] = useState([]);
  const [aboutData, setAboutData] = useState(null);
  const [aboutForm, setAboutForm] = useState(null);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  
  // Search and filter state - with proper separation to prevent re-renders
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // Local input state
  const [debouncedSearch, setDebouncedSearch] = useState(''); // Actual filter state
  const searchInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Debounce the search term update (not the input display)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInputValue]);

  // Handle search input without losing focus
  const handleSearchChange = (e) => {
    setSearchInputValue(e.target.value);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated, activeTab]);

  // Re-fetch bookings when statusFilter changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'bookings') {
      fetchBookings();
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchConstants();
  }, []);

  const fetchConstants = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/constants`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setConstants(data);
      }
    } catch (err) {
      console.error('Error fetching constants:', err);
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await fetchStats();
      if (activeTab === 'bookings' || activeTab === 'dashboard') await fetchBookings();
      if (activeTab === 'contacts' || activeTab === 'dashboard') await fetchContacts();
      if (activeTab === 'programs') await fetchPrograms();
      if (activeTab === 'sessions') await fetchSessions();
      if (activeTab === 'media') await fetchMedia();
      if (activeTab === 'pages') { await fetchPages(); await fetchPagesList(); }
      if (activeTab === 'sections') { await fetchSections(); await fetchPagesList(); }
      if (activeTab === 'settings') await fetchSettings();
      if (activeTab === 'users' && isSuperAdmin()) await fetchUsers();
      if (activeTab === 'reviews') { await fetchReviews(); await fetchProgramsList(); }
      if (activeTab === 'digital-assets') await fetchDigitalAssets();
      if (activeTab === 'about-editor') await fetchAboutData();
      if (activeTab === 'gallery') await fetchGalleryItems();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/stats`, { headers: getAuthHeader() });
      if (response.ok) setStats(await response.json());
    } catch (err) { console.error('Error fetching stats:', err); }
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await fetch(`${backendUrl}/api/admin/bookings?${params}`, { headers: getAuthHeader() });
      if (response.ok) setBookings(await response.json());
    } catch (err) { console.error('Error fetching bookings:', err); }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/contacts`, { headers: getAuthHeader() });
      if (response.ok) setContacts(await response.json());
    } catch (err) { console.error('Error fetching contacts:', err); }
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/programs`, { headers: getAuthHeader() });
      if (response.ok) setPrograms(await response.json());
    } catch (err) { console.error('Error fetching programs:', err); }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/sessions`, { headers: getAuthHeader() });
      if (response.ok) setSessions(await response.json());
    } catch (err) { console.error('Error fetching sessions:', err); }
  };

  const fetchMedia = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/media`, { headers: getAuthHeader() });
      if (response.ok) setMedia(await response.json());
    } catch (err) { console.error('Error fetching media:', err); }
  };

  const fetchPages = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/pages`, { headers: getAuthHeader() });
      if (response.ok) setPages(await response.json());
    } catch (err) { console.error('Error fetching pages:', err); }
  };

  const fetchSections = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/sections`, { headers: getAuthHeader() });
      if (response.ok) setSections(await response.json());
    } catch (err) { console.error('Error fetching sections:', err); }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/settings`, { headers: getAuthHeader() });
      if (response.ok) setSiteSettings(await response.json());
    } catch (err) { console.error('Error fetching settings:', err); }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/users`, { headers: getAuthHeader() });
      if (response.ok) setUsers(await response.json());
    } catch (err) { console.error('Error fetching users:', err); }
  };

  const fetchReviews = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await fetch(`${backendUrl}/api/admin/reviews?${params}`, { headers: getAuthHeader() });
      if (response.ok) setReviews(await response.json());
    } catch (err) { console.error('Error fetching reviews:', err); }
  };

  const fetchDigitalAssets = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/digital-assets`, { headers: getAuthHeader() });
      if (response.ok) setDigitalAssets(await response.json());
    } catch (err) { console.error('Error fetching digital assets:', err); }
  };

  const fetchPagesList = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/pages-list`, { headers: getAuthHeader() });
      if (response.ok) setPagesList(await response.json());
    } catch (err) { console.error('Error fetching pages list:', err); }
  };

  const fetchProgramsList = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/programs-list`, { headers: getAuthHeader() });
      if (response.ok) setProgramsList(await response.json());
    } catch (err) { console.error('Error fetching programs list:', err); }
  };

  const fetchAboutData = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/about`, { headers: getAuthHeader() });
      if (response.ok) {
        const data = await response.json();
        setAboutData(data);
        setAboutForm(JSON.parse(JSON.stringify(data)));
      }
    } catch (err) { console.error('Error fetching about data:', err); }
  };

  const fetchGalleryItems = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/media?folder=gallery`, { headers: getAuthHeader() });
      if (response.ok) setGalleryItems(await response.json());
    } catch (err) { console.error('Error fetching gallery items:', err); }
  };

  // File upload function
  const uploadFile = async (file, folder = 'general') => {
    if (!file) return null;
    
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeader().Authorization },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(null), 1000);
        return data;
      } else {
        const errText = await response.text();
        let errMsg = 'Upload failed';
        try { errMsg = JSON.parse(errText).detail || errMsg; } catch {}
        throw new Error(errMsg);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadProgress(null);
      alert('Upload failed: ' + err.message);
      return null;
    }
  };

  // Ref that always points to the latest uploadFile — used inside memoized Quill handlers
  // so the modules object is never recreated (avoids Quill wiping content on re-render)
  const uploadFileRef = useRef(uploadFile);
  uploadFileRef.current = uploadFile;

  // One ref per editor — accessed at call time inside the handler (not at memo creation time)
  const quillRefPageEN = useRef(null);
  const quillRefPageAR = useRef(null);
  const quillRefSectionEN = useRef(null);

  // Build a stable Quill image handler for a given folder.
  // Uses ref.current.getEditor() instead of this.quill to avoid stale instance issues
  // caused by the focus-blur cycle when the OS file picker opens.
  // getSelection() is called WITHOUT true so it does NOT force-focus the editor
  // (getSelection(true) was emitting a spurious onChange('') that cleared the editor).
  const buildImageHandler = (folder, quillRef) => function() {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const sel = quill.getSelection();
    const savedIndex = sel ? sel.index : quill.getLength();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const result = await uploadFileRef.current(file, folder);
        if (result && result.url) {
          const url = result.url.startsWith('/') ? `${backendUrl}${result.url}` : result.url;
          const freshQuill = quillRef.current ? quillRef.current.getEditor() : null;
          if (!freshQuill) return;
          freshQuill.focus();
          const currentSel = freshQuill.getSelection();
          const insertAt = currentSel ? currentSel.index : savedIndex;
          freshQuill.insertEmbed(insertAt, 'image', url);
          freshQuill.setSelection(insertAt + 1);
        }
      } catch (error) {
        console.error('Failed to insert image into Quill:', error);
      }
    };
  };

  const QUILL_TOOLBAR_CONTAINER = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: [1, 2, 3, false] }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    [{ script: 'sub' }, { script: 'super' }],
    ['link', 'image'],
    ['clean']
  ];

  const QUILL_FORMATS = [
    'bold', 'italic', 'underline', 'strike',
    'header',
    'color', 'background',
    'align',
    'list', 'indent',
    'blockquote', 'code-block',
    'script',
    'link', 'image'
  ];

  // Three distinct stable module configs — one per editor, each bound to its own ref.
  // Created ONCE (empty deps), never recreated. Prevents Quill remount on re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quillModulesPageEN = useMemo(() => ({
    toolbar: { container: QUILL_TOOLBAR_CONTAINER, handlers: { image: buildImageHandler('pages', quillRefPageEN) } }
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quillModulesPageAR = useMemo(() => ({
    toolbar: { container: QUILL_TOOLBAR_CONTAINER, handlers: { image: buildImageHandler('pages', quillRefPageAR) } }
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quillModulesSectionEN = useMemo(() => ({
    toolbar: { container: QUILL_TOOLBAR_CONTAINER, handlers: { image: buildImageHandler('sections', quillRefSectionEN) } }
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // CRUD Operations - Fixed to handle content fields as objects
  const createItem = async (type, data) => {
    const cmsTypes = ['programs', 'sessions', 'pages', 'sections', 'media'];
    const endpoint = cmsTypes.includes(type) ? `cms/${type}` : type;
    
    // Process data for pages and sections - send plain HTML strings
    let processedData = {...data};
    if (type === 'pages' || type === 'sections') {
      // Send content as plain HTML string (backend now accepts strings directly)
      if (processedData.content_en !== undefined) {
        processedData.content = processedData.content_en;
      }
      // content_ar is already a plain HTML string from Quill onChange
      // Ensure title fields are properly set
      if (processedData.title_en) {
        processedData.title = processedData.title_en;
      }
    }
    if (type === 'programs') {
      processedData.status = processedData.is_active === false ? 'draft' : 'published';
      delete processedData.is_active;
    }
    if (type === 'pages') {
      processedData.status = processedData.is_published !== false ? 'published' : 'draft';
      processedData.meta = { ...(processedData.meta || {}), template: processedData.template || 'default', title_ar: processedData.title_ar || '' };
      delete processedData.is_published;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/${endpoint}`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(processedData)
      });
      
      const responseText = await response.text();
      let result;
      try { result = JSON.parse(responseText); } catch { result = {}; }
      
      if (response.ok) {
        setShowModal(false);
        setSelectedItem(null);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1, -1)} created successfully!`);
        try {
          await fetchAllData();
        } catch (refreshErr) {
          console.error('List refresh failed after create:', refreshErr);
          toast.info('Item created successfully, but the list refresh failed. Please reload the page.');
        }
      } else if (response.status === 409) {
        const message = result.detail || 'This URL slug is already in use. Please choose a unique slug for this page.';
        toast.error(message);
      } else {
        const errorMessage = result.detail || (Array.isArray(result) && result[0]?.msg) || 'Error creating item';
        toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } catch (err) {
      console.error('Error creating item:', err);
      toast.error('Failed to send request. Please check your connection and try again.');
    }
  };

  const updateItem = async (type, id, data) => {
    const cmsTypes = ['programs', 'sessions', 'pages', 'sections', 'media'];
    let endpoint = cmsTypes.includes(type) ? `cms/${type}` : type;
    if (type === 'reviews') endpoint = 'reviews';
    if (type === 'digital-assets') endpoint = 'digital-assets';
    
    // Process data for pages and sections - send plain HTML strings
    let processedData = {...data};
    if (type === 'pages' || type === 'sections') {
      if (processedData.content_en !== undefined) {
        processedData.content = processedData.content_en;
      }
      // content_ar is already a plain HTML string from Quill onChange
      if (processedData.title_en) {
        processedData.title = processedData.title_en;
      }
    }
    if (type === 'programs' && 'is_active' in processedData) {
      processedData.status = processedData.is_active === false ? 'draft' : 'published';
      delete processedData.is_active;
    }
    if (type === 'sections' && 'is_visible' in processedData) {
      processedData.status = processedData.is_visible === false ? 'draft' : 'published';
      delete processedData.is_visible;
    }
    if (type === 'pages') {
      processedData.status = processedData.is_published !== false ? 'published' : 'draft';
      processedData.meta = { ...(processedData.meta || {}), template: processedData.template || 'default', title_ar: processedData.title_ar || '' };
      delete processedData.is_published;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/${endpoint}/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(processedData)
      });
      
      const responseText = await response.text();
      let result;
      try { result = JSON.parse(responseText); } catch { result = {}; }
      
      if (response.ok) {
        await fetchAllData();
        setShowModal(false);
        setSelectedItem(null);
        toast.success('Changes saved successfully!');
      } else if (response.status === 409) {
        toast.error(result.detail || 'A page with this slug already exists. Please choose a different one.');
      } else {
        const errorMessage = result.detail || 'Error saving changes';
        toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } catch (err) {
      console.error('Error updating item:', err);
      toast.error('Network error. Please try again.');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    const cmsTypes = ['programs', 'sessions', 'pages', 'sections', 'media'];
    let endpoint = cmsTypes.includes(type) ? `cms/${type}` : type;
    if (type === 'reviews') endpoint = 'reviews';
    if (type === 'digital-assets') endpoint = 'digital-assets';
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        await fetchAllData();
        toast.success('Item deleted successfully!');
      } else if (response.status === 403) {
        const permMsg = type === 'media'
          ? 'Only super admins can delete media.'
          : 'You do not have permission to delete this item.';
        toast.error(permMsg);
      } else {
        const err = await response.text();
        let errMsg = 'Error deleting item';
        try { errMsg = JSON.parse(err).detail || errMsg; } catch {}
        toast.error(errMsg);
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      toast.error('Unable to delete item. Please try again.');
    }
  };

  const downloadMedia = (item) => {
    const downloadUrl = item.url.replace('/api/uploads/', '/api/downloads/');
    const a = document.createElement('a');
    a.href = downloadUrl + (item.filename ? `?original_name=${encodeURIComponent(item.filename)}` : '');
    a.download = item.filename || item.name || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const publishItem = async (type, id) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/${type}/${id}/publish`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (response.ok) {
        await fetchAllData();
        toast.success('Item published successfully!');
      } else {
        toast.error('Error publishing item');
      }
    } catch (err) {
      console.error('Error publishing:', err);
      toast.error('Unable to publish item');
    }
  };

  // Export CSV function - Fixed
  const exportToCSV = async (type) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/${type}/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        alert('Export failed');
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Export failed: ' + err.message);
    }
  };

  // Review moderation
  const approveReview = async (id) => {
    try {
      await fetch(`${backendUrl}/api/admin/reviews/${id}/approve`, { method: 'POST', headers: getAuthHeader() });
      await fetchReviews();
      alert('Review approved!');
    } catch (err) { console.error('Error:', err); }
  };

  const rejectReview = async (id) => {
    try {
      await fetch(`${backendUrl}/api/admin/reviews/${id}/reject`, { method: 'POST', headers: getAuthHeader() });
      await fetchReviews();
      alert('Review rejected');
    } catch (err) { console.error('Error:', err); }
  };

  const featureReview = async (id) => {
    try {
      await fetch(`${backendUrl}/api/admin/reviews/${id}/feature`, { method: 'POST', headers: getAuthHeader() });
      await fetchReviews();
      alert('Review featured!');
    } catch (err) { console.error('Error:', err); }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/bookings/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        await fetchBookings();
        await fetchStats();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const updateContactStatus = async (id, status) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/contacts/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        await fetchContacts();
        await fetchStats();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const updateSettings = async (data) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/cms/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        await fetchSettings();
        alert('Settings saved!');
      }
    } catch (err) { console.error('Error:', err); }
  };

  const getTextContent = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.en) return value.en;
      if (value.title_en) return value.title_en;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const defaultFormData = {
    username: '', password: '', email: '', full_name: '', role: 'editor',
    title: '', arabic_title: '', description: '', badge: '', format: '', cta: '', image_url: '', is_active: true,
    title_en: '', title_ar: '', description_en: '', description_ar: '', price: '', duration: '', duration_minutes: 60, benefits: [],
    name: '', url: '', alt_text: '', category: 'general', folder: 'general', filename: '', file_type: 'image',
    slug: '', content_en: '', content_ar: '', is_published: true, template: 'default',
    page_id: '', section_type: 'custom', subtitle_en: '', subtitle_ar: '', cta_text_en: '', cta_text_ar: '', cta_link: '', is_visible: true,
    investment: {}, investment_ar: {}, focus: [], focus_ar: [], outcomes: [], capacity: null, schedule: '',
    session_type: 'crystal-healing', currency: 'EGP', is_featured: false, expectations: [],
    rating: 5, content: '', status: 'pending', program_id: '', program_name: '', admin_notes: '',
    file_url: '', challenge_name: 'Filter Challenge', access_code: '', requires_code: false,
    start_date: '', start_time: ''
  };

  const getInitialFormData = useCallback((currentModalType) => {
    if (!selectedItem) return { ...defaultFormData };
    const merged = { ...defaultFormData };
    Object.keys(selectedItem).forEach(key => {
      const value = selectedItem[key];
      if (value !== null && value !== undefined) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          if (key === 'title') {
            merged[key] = getTextContent(value);
          } else if (key === 'content') {
            const html = (value && typeof value === 'object') ? (value.text || '') : (value || '');
            merged['content'] = html;
            merged['content_en'] = html;
          } else if (key === 'content_ar') {
            merged['content_ar'] = (value && typeof value === 'object') ? (value.text || '') : (value || '');
          } else {
            merged[key] = value;
          }
        } else {
          merged[key] = value;
          if (key === 'content' && typeof value === 'string') {
            merged['content_en'] = value;
          }
        }
      }
    });
    if (selectedItem.status !== undefined && merged.is_active === true) {
      merged.is_active = selectedItem.status === 'published';
    }
    if (selectedItem.section_type !== undefined) {
      merged.is_visible = selectedItem.status !== 'draft';
    }
    if (currentModalType === 'editPage') {
      merged.is_published = selectedItem.status === 'published';
      if (selectedItem.meta?.template) {
        merged.template = selectedItem.meta.template;
      }
      if (selectedItem.meta?.title_ar) {
        merged.title_ar = selectedItem.meta.title_ar;
      }
    }
    return merged;
  }, [selectedItem]);

  const [formData, setFormData] = useState(() => getInitialFormData(modalType));

  useEffect(() => {
    setFormData(getInitialFormData(modalType));
  }, [selectedItem, modalType, getInitialFormData]);

  useEffect(() => {
    if (!selectedItem) return;
    const injectContent = (ref, html) => {
      if (!ref.current || !html) return;
      try {
        const editor = ref.current.getEditor();
        if (editor && editor.root.innerHTML !== html) {
          editor.clipboard.dangerouslyPasteHTML(html);
        }
      } catch (e) {}
    };
    if (modalType === 'editPage') {
      injectContent(quillRefPageEN, selectedItem.content || '');
      injectContent(quillRefPageAR, selectedItem.content_ar || '');
    } else if (modalType === 'editSection') {
      injectContent(quillRefSectionEN, selectedItem.content_en || selectedItem.content || '');
    }
  }, [selectedItem?.id, modalType]);

  if (!isAuthenticated) return <AdminLogin />;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (modalType === 'createUser') createItem('users', formData);
    else if (modalType === 'createProgram') createItem('programs', formData);
    else if (modalType === 'createSession') createItem('sessions', formData);
    else if (modalType === 'createMedia') createItem('media', formData);
    else if (modalType === 'createPage') createItem('pages', formData);
    else if (modalType === 'createSection') createItem('sections', formData);
    else if (modalType === 'createDigitalAsset') createItem('digital-assets', formData);
    else if (modalType === 'editProgram') updateItem('programs', selectedItem.id, formData);
    else if (modalType === 'editSession') updateItem('sessions', selectedItem.id, formData);
    else if (modalType === 'editMedia') updateItem('media', selectedItem.id, formData);
    else if (modalType === 'editPage') updateItem('pages', selectedItem.id, formData);
    else if (modalType === 'editSection') updateItem('sections', selectedItem.id, formData);
    else if (modalType === 'editUser') updateItem('users', selectedItem.id, formData);
    else if (modalType === 'editReview') updateItem('reviews', selectedItem.id, formData);
    else if (modalType === 'editDigitalAsset') updateItem('digital-assets', selectedItem.id, formData);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'contacts', label: 'Messages', icon: MessageSquare },
    { id: 'reviews', label: 'Reviews', icon: Star, superAdminOnly: true },
    { id: 'programs', label: 'Programs', icon: BookOpen, superAdminOnly: true },
    { id: 'sessions', label: 'Sessions', icon: Layers, superAdminOnly: true },
    { id: 'pages', label: 'Pages', icon: FileText, superAdminOnly: true },
    { id: 'sections', label: 'Sections', icon: Layout, superAdminOnly: true },
    { id: 'media', label: 'Media', icon: Image, superAdminOnly: true },
    { id: 'gallery', label: 'Gallery', icon: Image, superAdminOnly: true },
    { id: 'digital-assets', label: 'Digital Files', icon: FileDown, superAdminOnly: true },
    { id: 'challenges', label: 'Challenges', icon: Play, superAdminOnly: true },
    { id: 'about-editor', label: 'About Page', icon: Users, superAdminOnly: true },
    { id: 'settings', label: 'Settings', icon: Settings, superAdminOnly: true },
    { id: 'users', label: 'Users', icon: Users, superAdminOnly: true }
  ];

  const filteredTabs = tabs.filter(tab => !tab.superAdminOnly || isSuperAdmin());

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      confirmed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      unread: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      read: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      replied: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      featured: 'bg-gold/20 text-gold border-gold/30',
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };


  // ═══════════════════════════════════════════════════════════════════════════
  // STABLE FILE UPLOAD COMPONENT - Never triggers form reset
  // ═══════════════════════════════════════════════════════════════════════════
  const StableFileUpload = React.memo(({ onUpload, folder = 'general', currentUrl = '', label = 'Upload Image' }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [localPreview, setLocalPreview] = useState(currentUrl);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const fileInputRef = useRef(null);

    // Sync with external currentUrl changes (e.g., when editing)
    useEffect(() => {
      if (currentUrl && currentUrl !== localPreview) {
        setLocalPreview(currentUrl);
      }
    }, [currentUrl]);

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    
    const processFile = async (file) => {
      if (!file) return;
      
      setIsUploading(true);
      setUploadedFileName(file.name);
      
      // Create local preview immediately for instant feedback
      const reader = new FileReader();
      reader.onload = (e) => {
        if (file.type.startsWith('image/')) {
          setLocalPreview(e.target.result);
        }
      };
      reader.readAsDataURL(file);
      
      // Upload to server
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        
        const response = await fetch(`${backendUrl}/api/admin/upload`, {
          method: 'POST',
          headers: { 'Authorization': getAuthHeader().Authorization },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setLocalPreview(result.url);
          onUpload(result.url);
          toast.success('File uploaded successfully!');
        } else {
          const errorText = await response.text();
          let errorMsg = 'Upload failed. Please try again.';
          try {
            const errorData = JSON.parse(errorText);
            errorMsg = errorData.detail || errorMsg;
          } catch {}
          console.error('Upload error response:', errorText);
          toast.error(errorMsg);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error('Upload failed. Please check your connection.');
      } finally {
        setIsUploading(false);
      }
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      await processFile(file);
    };

    const handleFileSelect = async (e) => {
      e.stopPropagation();
      const file = e.target.files?.[0];
      await processFile(file);
      // Reset input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClick = (e) => {
      e.stopPropagation();
      fileInputRef.current?.click();
    };

    return (
      <div className="space-y-2">
        <label className="block text-cream-400 text-xs uppercase tracking-wider">{label}</label>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all min-h-[120px] flex flex-col items-center justify-center ${isDragging ? 'border-gold bg-gold/10' : 'border-white/20 hover:border-gold/50'}`}
        >
          <input 
            ref={fileInputRef} 
            type="file" 
            accept="image/*,video/*,.pdf,.mp3,.mp4,.wav" 
            onChange={handleFileSelect} 
            className="hidden" 
            onClick={(e) => e.stopPropagation()}
          />
          
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              <p className="text-gold text-sm">Uploading {uploadedFileName}...</p>
            </div>
          ) : localPreview ? (
            <div className="relative w-full">
              {localPreview.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || localPreview.startsWith('data:image') ? (
                <img src={localPreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
              ) : (
                <div className="flex items-center justify-center h-32 bg-charcoal-100 rounded-lg">
                  <FileDown className="text-gold mr-2" size={24} />
                  <span className="text-cream text-sm">{uploadedFileName || 'File attached'}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                <Upload className="text-white" size={24} />
                <span className="text-white ml-2 text-sm">Change File</span>
              </div>
            </div>
          ) : (
            <>
              <Upload className={`mb-2 ${isDragging ? 'text-gold' : 'text-cream-400'}`} size={24} />
              <p className="text-cream-400 text-sm">Drag & drop or click to upload</p>
              <p className="text-cream-500 text-xs mt-1">PNG, JPG, GIF, PDF, MP3 up to 10MB</p>
            </>
          )}
        </div>
      </div>
    );
  });

  // Color Picker Component
  const ColorPickerInput = ({ value, onChange, label }) => {
    const [showPicker, setShowPicker] = useState(false);
    return (
      <div className="relative">
        <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">{label}</label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="w-10 h-10 rounded-lg border border-white/20 hover:border-gold transition-colors cursor-pointer"
            style={{ backgroundColor: value || '#F3D222' }}
          />
          <input
            type="text"
            value={value || '#F3D222'}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 px-3 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream text-sm font-mono"
          />
        </div>
        {showPicker && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
            <div className="absolute z-50 mt-2 left-0">
              <SketchPicker
                color={value || '#F3D222'}
                onChange={(c) => onChange(c.hex)}
                presetColors={['#F3D222', '#1a1a1a', '#f5f0e8', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6']}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Dashboard View
  const DashboardView = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-cream">Dashboard Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => setActiveTab('bookings')} className="p-5 rounded-xl border bg-gold/10 text-gold border-gold/20 hover:bg-gold/20 transition-all text-left">
          <div className="flex items-center justify-between"><div><p className="text-cream-400 text-sm">Bookings</p><p className="text-3xl font-bold mt-1">{stats.bookings?.total || 0}</p></div><Calendar size={32} className="opacity-50" /></div>
        </button>
        <button onClick={() => { setStatusFilter('pending'); setActiveTab('bookings'); }} className="p-5 rounded-xl border bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20 transition-all text-left">
          <div className="flex items-center justify-between"><div><p className="text-cream-400 text-sm">Pending</p><p className="text-3xl font-bold mt-1">{stats.bookings?.pending || 0}</p></div><Clock size={32} className="opacity-50" /></div>
        </button>
        <button onClick={() => setActiveTab('contacts')} className="p-5 rounded-xl border bg-purple-500/10 text-purple-400 border-purple-500/20 hover:bg-purple-500/20 transition-all text-left">
          <div className="flex items-center justify-between"><div><p className="text-cream-400 text-sm">Messages</p><p className="text-3xl font-bold mt-1">{stats.contacts?.total || 0}</p></div><MessageSquare size={32} className="opacity-50" /></div>
        </button>
        <button onClick={() => setActiveTab('reviews')} className="p-5 rounded-xl border bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/20 transition-all text-left">
          <div className="flex items-center justify-between"><div><p className="text-cream-400 text-sm">Reviews Pending</p><p className="text-3xl font-bold mt-1">{stats.reviews?.pending || 0}</p></div><Star size={32} className="opacity-50" /></div>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-charcoal border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-cream">Recent Bookings</h3>
            <button onClick={() => setActiveTab('bookings')} className="text-gold text-sm hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {bookings.slice(0, 5).map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 bg-charcoal-100 rounded-lg">
                <div><p className="text-cream font-medium">{b.full_name}</p><p className="text-cream-400 text-sm">{b.preferred_date} • {b.preferred_time}</p></div>
                <span className={`px-2 py-1 text-xs rounded-md border ${getStatusColor(b.status)}`}>{b.status}</span>
              </div>
            ))}
            {bookings.length === 0 && <p className="text-cream-400 text-center py-4">No bookings yet</p>}
          </div>
        </div>
        <div className="bg-charcoal border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-cream">Recent Messages</h3>
            <button onClick={() => setActiveTab('contacts')} className="text-gold text-sm hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {contacts.slice(0, 5).map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-charcoal-100 rounded-lg">
                <div><p className="text-cream font-medium">{c.name}</p><p className="text-cream-400 text-sm truncate max-w-[200px]">{c.subject}</p></div>
                <span className={`px-2 py-1 text-xs rounded-md border ${getStatusColor(c.status)}`}>{c.status}</span>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-cream-400 text-center py-4">No messages yet</p>}
          </div>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // UNBREAKABLE SEARCH BAR COMPONENT - Completely isolated from parent state
  // ═══════════════════════════════════════════════════════════════════════════
  const UnbreakableSearchBar = React.memo(({ onSearchChange, placeholder = "Search clients..." }) => {
    // Completely local state - never triggers parent re-render on keystroke
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // Handle every keystroke - updates local state ONLY
    const handleInputChange = useCallback((e) => {
      const newValue = e.target.value;
      setLocalSearchTerm(newValue);
      
      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set new 500ms debounce timer before notifying parent
      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(newValue);
      }, 500);
    }, [onSearchChange]);

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={localSearchTerm}
          onChange={handleInputChange}
          className="bg-charcoal-100 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-cream text-sm w-64 focus:border-gold/50 focus:outline-none transition-colors"
          autoComplete="off"
        />
      </div>
    );
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOKINGS VIEW - Uses UnbreakableSearchBar with proper state isolation
  // ═══════════════════════════════════════════════════════════════════════════
  const BookingsView = () => {
    // Local filter state - completely isolated from search input
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [activeStatusFilter, setActiveStatusFilter] = useState('');

    // Callback for search - only called after 500ms debounce
    const handleSearchUpdate = useCallback((term) => {
      setActiveSearchTerm(term);
    }, []);

    // Filter bookings using the debounced search term
    const filteredBookings = useMemo(() => {
      return bookings.filter(b => {
        // Status filter
        if (activeStatusFilter && b.status !== activeStatusFilter) return false;
        
        // Search filter (only applied after debounce)
        if (activeSearchTerm) {
          const search = activeSearchTerm.toLowerCase();
          const matchesName = b.full_name?.toLowerCase().includes(search);
          const matchesEmail = b.email?.toLowerCase().includes(search);
          const matchesSession = b.session_type?.toLowerCase().includes(search);
          if (!matchesName && !matchesEmail && !matchesSession) return false;
        }
        
        return true;
      });
    }, [bookings, activeSearchTerm, activeStatusFilter]);

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-3">
            {/* Status filter dropdown */}
            <select 
              value={activeStatusFilter} 
              onChange={(e) => setActiveStatusFilter(e.target.value)} 
              className="bg-charcoal-100 border border-white/10 rounded-lg px-4 py-2 text-cream text-sm focus:border-gold/50 focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
            
            {/* Unbreakable Search Bar - typing never loses focus */}
            <UnbreakableSearchBar 
              onSearchChange={handleSearchUpdate}
              placeholder="Search clients..."
            />
          </div>
          <div className="flex gap-3">
            <button onClick={fetchBookings} className="flex items-center gap-2 px-4 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream-300 hover:text-gold transition-colors"><RefreshCw size={16} /> Refresh</button>
            <button onClick={() => exportToCSV('bookings')} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500 transition-colors"><Download size={16} /> Export CSV</button>
          </div>
        </div>
        <div className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-charcoal-100">
                <tr>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Client</th>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Session</th>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredBookings.map(booking => (
                  <tr key={booking.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-4"><p className="text-cream font-medium">{booking.full_name}</p><p className="text-cream-400 text-sm">{booking.email}</p></td>
                    <td className="px-4 py-4 text-cream-300">{booking.session_type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                    <td className="px-4 py-4 text-cream-300">{booking.preferred_date}</td>
                    <td className="px-4 py-4 text-gold font-medium">{booking.preferred_time}</td>
                    <td className="px-4 py-4">
                      <select value={booking.status} onChange={(e) => updateBookingStatus(booking.id, e.target.value)} className={`px-2 py-1 text-xs rounded-md border ${getStatusColor(booking.status)} bg-transparent cursor-pointer`}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => { setSelectedItem(booking); setModalType('viewBooking'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5 transition-colors"><Eye size={16} /></button>
                        <button onClick={() => deleteItem('bookings', booking.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5 transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredBookings.length === 0 && <div className="text-center py-12 text-cream-400">No bookings found</div>}
        </div>
      </div>
    );
  };

  // Contacts View
  const ContactsView = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-charcoal-100 border border-white/10 rounded-lg px-4 py-2 text-cream text-sm">
          <option value="">All Status</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={() => exportToCSV('contacts')} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><Download size={16} /> Export CSV</button>
      </div>
      <div className="space-y-4">
        {contacts.filter(c => !statusFilter || c.status === statusFilter).map(contact => (
          <div key={contact.id} className="bg-charcoal border border-white/10 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-cream font-medium">{contact.name}</h4>
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(contact.status)}`}>{contact.status}</span>
                </div>
                <p className="text-cream-400 text-sm mb-1">{contact.email}</p>
                <p className="text-gold text-sm font-medium mb-2">{contact.subject}</p>
                <p className="text-cream-300 text-sm">{contact.message}</p>
                <p className="text-cream-500 text-xs mt-3">{formatDate(contact.created_at)}</p>
              </div>
              <div className="flex flex-col gap-2">
                <select value={contact.status} onChange={(e) => updateContactStatus(contact.id, e.target.value)} className="bg-charcoal-100 border border-white/10 rounded-lg px-3 py-1 text-cream text-xs">
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                </select>
                <button onClick={() => deleteItem('contacts', contact.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {contacts.length === 0 && <div className="text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No messages yet</div>}
      </div>
    </div>
  );

  // Reviews View - With program reassignment
  const ReviewsView = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-charcoal-100 border border-white/10 rounded-lg px-4 py-2 text-cream text-sm">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="featured">Featured</option>
        </select>
        <button onClick={() => exportToCSV('reviews')} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><Download size={16} /> Export CSV</button>
      </div>
      <div className="space-y-4">
        {reviews.filter(r => !statusFilter || r.status === statusFilter).map(review => (
          <div key={review.id} className="bg-charcoal border border-white/10 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-cream font-medium">{review.name}</h4>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < review.rating ? 'text-gold fill-gold' : 'text-cream-500'} />)}
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(review.status)}`}>{review.status}</span>
                </div>
                {review.program_name && <p className="text-purple-400 text-sm mb-1">Program: {review.program_name}</p>}
                {review.title && <p className="text-gold text-sm font-medium mb-2">{review.title}</p>}
                <p className="text-cream-300 text-sm">{review.content}</p>
                <p className="text-cream-500 text-xs mt-3">{formatDate(review.created_at)}</p>
              </div>
              <div className="flex flex-col gap-2">
                {review.status === 'pending' && (
                  <>
                    <button onClick={() => approveReview(review.id)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-xs hover:bg-emerald-500/30"><CheckCircle size={14} /> Approve</button>
                    <button onClick={() => rejectReview(review.id)} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/30"><XCircle size={14} /> Reject</button>
                  </>
                )}
                {review.status === 'approved' && <button onClick={() => featureReview(review.id)} className="flex items-center gap-1 px-3 py-1.5 bg-gold/20 border border-gold/30 rounded-lg text-gold text-xs hover:bg-gold/30"><Star size={14} /> Feature</button>}
                <button onClick={() => { setSelectedItem(review); setModalType('editReview'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5"><Edit2 size={16} /></button>
                <button onClick={() => deleteItem('reviews', review.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && <div className="text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No reviews yet</div>}
      </div>
    </div>
  );

  // Programs View
  const ProgramsView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Manage Programs</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createProgram'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><PlusCircle size={16} /> Add Program</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {programs.map(program => (
          <div key={program.id} className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
            {program.image_url && <img src={program.image_url} alt={program.title} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                {program.badge && <span className="px-2 py-0.5 text-xs rounded bg-gold/20 text-gold">{program.badge}</span>}
                <span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(program.status || 'draft')}`}>{program.status || 'draft'}</span>
              </div>
              <h4 className="text-cream font-medium mb-2">{program.title}</h4>
              <p className="text-cream-400 text-sm line-clamp-2">{program.description}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => { setSelectedItem(program); setModalType('editProgram'); setShowModal(true); }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream-300 hover:text-gold text-sm"><Edit2 size={14} /> Edit</button>
                {program.status !== 'published' && <button onClick={() => publishItem('programs', program.id)} className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm"><CheckCircle size={14} /> Publish</button>}
                <button onClick={() => deleteItem('programs', program.id)} className="px-3 py-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {programs.length === 0 && <div className="col-span-full text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No programs yet</div>}
      </div>
    </div>
  );

  // Sessions View
  const SessionsView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Manage Sessions</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createSession'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><PlusCircle size={16} /> Add Session</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sessions.map(session => (
          <div key={session.id} className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
            {session.image_url && <img src={session.image_url} alt={session.title || session.title_en} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-cream font-medium">{session.title || session.title_en}</h4>
                <span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(session.status || 'draft')}`}>{session.status || 'draft'}</span>
              </div>
              {session.price && <p className="text-gold text-sm mb-2">{session.currency || 'EGP'} {session.price}</p>}
              {session.duration_minutes && <p className="text-cream-400 text-sm">Duration: {session.duration_minutes} mins</p>}
              <div className="flex gap-2 mt-4">
                <button onClick={() => { setSelectedItem(session); setModalType('editSession'); setShowModal(true); }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream-300 hover:text-gold text-sm"><Edit2 size={14} /> Edit</button>
                {session.status !== 'published' && <button onClick={() => publishItem('sessions', session.id)} className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm"><CheckCircle size={14} /> Publish</button>}
                <button onClick={() => deleteItem('sessions', session.id)} className="px-3 py-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {sessions.length === 0 && <div className="col-span-full text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No sessions yet</div>}
      </div>
    </div>
  );

  // Pages View - Fixed [object Object] rendering
  const PagesView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Manage Pages</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createPage'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><PlusCircle size={16} /> Add Page</button>
      </div>
      <div className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-charcoal-100">
            <tr>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Page</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Slug</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {pages.map(page => (
              <tr key={page.id} className="hover:bg-white/5">
                <td className="px-4 py-4"><p className="text-cream font-medium">{getTextContent(page.title) || getTextContent(page.title_en) || page.slug}</p></td>
                <td className="px-4 py-4 text-cream-400">/{page.slug}</td>
                <td className="px-4 py-4"><span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(page.status || 'draft')}`}>{page.status || 'draft'}</span></td>
                <td className="px-4 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedItem(page); setModalType('editPage'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5"><Edit2 size={16} /></button>
                    {page.status !== 'published' && <button onClick={() => publishItem('pages', page.id)} className="p-2 text-emerald-400 hover:bg-emerald-500/20 rounded-lg"><CheckCircle size={16} /></button>}
                    <button onClick={() => deleteItem('pages', page.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages.length === 0 && <div className="text-center py-12 text-cream-400">No pages yet</div>}
      </div>
    </div>
  );

  // Sections View
  const SectionsView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Manage Sections</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createSection'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><PlusCircle size={16} /> Add Section</button>
      </div>
      <div className="space-y-4">
        {sections.map(section => (
          <div key={section.id} className="bg-charcoal border border-white/10 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 text-xs rounded-md bg-purple-500/20 text-purple-400">{section.section_type || 'custom'}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${getStatusColor(section.status || 'draft')}`}>{section.status || 'draft'}</span>
                </div>
                <h4 className="text-cream font-medium mb-1">{getTextContent(section.title) || getTextContent(section.title_en) || 'Untitled Section'}</h4>
                {section.title_ar && <p className="text-gold text-sm">{section.title_ar}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setSelectedItem(section); setModalType('editSection'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5"><Edit2 size={16} /></button>
                {section.status !== 'published' && <button onClick={() => publishItem('sections', section.id)} className="p-2 text-emerald-400 hover:bg-emerald-500/20 rounded-lg"><CheckCircle size={16} /></button>}
                <button onClick={() => deleteItem('sections', section.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {sections.length === 0 && <div className="text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No sections yet</div>}
      </div>
    </div>
  );

  // Media View with File Upload
  const MediaView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Media Library</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createMedia'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><Upload size={16} /> Upload Media</button>
      </div>
      <StableFileUpload onUpload={async (url) => { await fetchMedia(); alert('File uploaded successfully!'); }} folder="general" label="Quick Upload" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {media.map(item => (
          <div key={item.id} className="bg-charcoal border border-white/10 rounded-xl overflow-hidden group">
            <div className="aspect-square relative">
              <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => { setSelectedItem(item); setModalType('viewMedia'); setShowModal(true); }} className="p-2 bg-white/10 rounded-lg text-cream hover:text-gold" title="View"><Eye size={16} /></button>
                <button onClick={() => downloadMedia(item)} className="p-2 bg-white/10 rounded-lg text-cream hover:text-gold" title="Download"><Download size={16} /></button>
                <button onClick={() => { setSelectedItem(item); setModalType('editMedia'); setShowModal(true); }} className="p-2 bg-white/10 rounded-lg text-cream hover:text-gold" title="Edit"><Edit2 size={16} /></button>
                <button onClick={() => deleteItem('media', item.id)} className="p-2 bg-white/10 rounded-lg text-cream hover:text-red-400" title="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="p-3"><p className="text-cream text-sm truncate">{item.name}</p><p className="text-cream-500 text-xs">{item.folder || 'general'}</p></div>
          </div>
        ))}
        {media.length === 0 && <div className="col-span-full text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No media yet</div>}
      </div>
    </div>
  );

  // Digital Assets View (for gated content)
  const DigitalAssetsView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">Digital Files & Downloads</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createDigitalAsset'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><PlusCircle size={16} /> Add Digital Asset</button>
      </div>
      <div className="space-y-4">
        {digitalAssets.map(asset => (
          <div key={asset.id} className="bg-charcoal border border-white/10 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <FileDown size={20} className="text-gold" />
                  <h4 className="text-cream font-medium">{asset.title}</h4>
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${asset.is_active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
                    {asset.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-cream-400 text-sm mb-2">{asset.description}</p>
                <p className="text-purple-400 text-sm">Challenge: {asset.challenge_name}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-2 px-3 py-1 bg-charcoal-100 rounded-lg">
                    <LinkIcon size={14} className="text-gold" />
                    <span className="text-cream-300 text-xs font-mono">/download/{asset.share_token}</span>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/download/${asset.share_token}`); alert('Link copied!'); }} className="p-1 text-cream-400 hover:text-gold"><Copy size={12} /></button>
                  </div>
                  <p className="text-cream-500 text-xs">Downloads: {asset.download_count || 0}</p>
                  {asset.access_code && <p className="text-cream-500 text-xs">Code: {asset.access_code}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setSelectedItem(asset); setModalType('editDigitalAsset'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5"><Edit2 size={16} /></button>
                <button onClick={() => deleteItem('digital-assets', asset.id)} className="p-2 text-cream-400 hover:text-red-400 rounded-lg hover:bg-white/5"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {digitalAssets.length === 0 && <div className="text-center py-12 text-cream-400 bg-charcoal border border-white/10 rounded-xl">No digital assets yet. Create one to share files with your subscribers!</div>}
      </div>
    </div>
  );

  // Settings View with Color Picker and Logo Upload
  const SettingsView = () => {
    const [localSettings, setLocalSettings] = useState(siteSettings);
    useEffect(() => { setLocalSettings(siteSettings); }, [siteSettings]);

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-cream">Site Settings</h3>
        <div className="bg-charcoal border border-white/10 rounded-xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Site Name</label><input type="text" value={localSettings.site_name || ''} onChange={(e) => setLocalSettings({...localSettings, site_name: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Tagline</label><input type="text" value={localSettings.tagline || ''} onChange={(e) => setLocalSettings({...localSettings, tagline: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Contact Email</label><input type="email" value={localSettings.contact_email || ''} onChange={(e) => setLocalSettings({...localSettings, contact_email: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Contact Phone</label><input type="text" value={localSettings.contact_phone || ''} onChange={(e) => setLocalSettings({...localSettings, contact_phone: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
          </div>
          <ColorPickerInput label="Primary Color" value={localSettings.primary_color} onChange={(color) => setLocalSettings({...localSettings, primary_color: color})} />
          <StableFileUpload label="Site Logo" currentUrl={localSettings.logo_url} onUpload={(url) => setLocalSettings({...localSettings, logo_url: url})} folder="general" />
          <StableFileUpload label="Favicon" currentUrl={localSettings.favicon_url} onUpload={(url) => setLocalSettings({...localSettings, favicon_url: url})} folder="general" />
          <div><label className="block text-cream-400 text-xs uppercase mb-2">Meta Description (SEO)</label><textarea value={localSettings.meta_description || ''} onChange={(e) => setLocalSettings({...localSettings, meta_description: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-24" placeholder="Brief description for search engines..." /></div>
          <div><label className="block text-cream-400 text-xs uppercase mb-2">Meta Keywords (SEO)</label><input type="text" value={localSettings.meta_keywords || ''} onChange={(e) => setLocalSettings({...localSettings, meta_keywords: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="keyword1, keyword2, keyword3" /></div>
          <h4 className="text-cream font-medium pt-4 border-t border-white/10">Social Media</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Instagram URL</label><input type="url" value={localSettings.social_instagram || ''} onChange={(e) => setLocalSettings({...localSettings, social_instagram: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
            <div><label className="block text-cream-400 text-xs uppercase mb-2">Facebook URL</label><input type="url" value={localSettings.social_facebook || ''} onChange={(e) => setLocalSettings({...localSettings, social_facebook: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
          </div>
          <button onClick={() => updateSettings(localSettings)} className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500 flex items-center justify-center gap-2"><Save size={16} /> Save Settings</button>
        </div>
      </div>
    );
  };

  // Users View
  const UsersView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-cream">User Management</h3>
        <button onClick={() => { setSelectedItem(null); setModalType('createUser'); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500"><UserPlus size={16} /> Add User</button>
      </div>
      <div className="bg-charcoal border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-charcoal-100">
            <tr>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">User</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Role</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 text-cream-400 text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-white/5">
                <td className="px-4 py-4"><p className="text-cream font-medium">{u.full_name || u.username}</p><p className="text-cream-400 text-sm">{u.email}</p></td>
                <td className="px-4 py-4"><span className={`px-2 py-0.5 text-xs rounded-md ${u.role === 'super_admin' ? 'bg-gold/20 text-gold' : 'bg-blue-500/20 text-blue-400'}`}>{u.role}</span></td>
                <td className="px-4 py-4"><span className={`px-2 py-0.5 text-xs rounded-md ${u.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedItem(u); setModalType('editUser'); setShowModal(true); }} className="p-2 text-cream-400 hover:text-gold rounded-lg hover:bg-white/5"><Edit2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="text-center py-12 text-cream-400">No users found</div>}
      </div>
    </div>
  );

  const SECTION_TYPES = ['hero', 'about', 'services', 'programs', 'testimonials', 'gallery', 'contact', 'cta', 'features', 'faq', 'team', 'pricing', 'stats', 'newsletter', 'banner', 'custom'];
  const MEDIA_FOLDERS = ['general', 'hero', 'programs', 'sessions', 'gallery', 'about', 'testimonials', 'team', 'blog', 'documents', 'digital-assets'];
  const PROGRAM_BADGES = ['Free', 'Premium', 'New', 'Popular', 'Limited', 'Exclusive'];
  const PROGRAM_FORMATS = ['Online', 'In-Person', 'Hybrid', 'Self-Paced', 'Group', '1-on-1'];
  const SESSION_TYPES = ['crystal-healing', 'meditation', 'coaching', 'workshop', 'consultation', 'group-session'];

  const sectionTypes = constants.section_types || SECTION_TYPES;
  const mediaFolders = constants.media_folders || MEDIA_FOLDERS;
  const programBadges = constants.program_badges || PROGRAM_BADGES;
  const programFormats = constants.program_formats || PROGRAM_FORMATS;
  const sessionTypes = constants.session_types || SESSION_TYPES;

  const modalContent = showModal ? (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-charcoal border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-charcoal z-10">
            <h3 className="text-xl font-semibold text-cream">
              {modalType === 'viewBooking' && 'Booking Details'}
              {modalType === 'createUser' && 'Create User'}
              {modalType === 'editUser' && 'Edit User'}
              {modalType === 'createProgram' && 'Create Program'}
              {modalType === 'editProgram' && 'Edit Program'}
              {modalType === 'createSession' && 'Create Session'}
              {modalType === 'editSession' && 'Edit Session'}
              {modalType === 'createMedia' && 'Upload Media'}
              {modalType === 'editMedia' && 'Edit Media'}
              {modalType === 'viewMedia' && 'Media Details'}
              {modalType === 'createPage' && 'Create Page'}
              {modalType === 'editPage' && 'Edit Page'}
              {modalType === 'createSection' && 'Create Section'}
              {modalType === 'editSection' && 'Edit Section'}
              {modalType === 'editReview' && 'Edit Review'}
              {modalType === 'createDigitalAsset' && 'Create Digital Asset'}
              {modalType === 'editDigitalAsset' && 'Edit Digital Asset'}
            </h3>
            <button onClick={() => { setShowModal(false); setSelectedItem(null); }} className="text-cream-400 hover:text-cream"><X size={24} /></button>
          </div>

          <div className="p-6">
            {/* View Booking */}
            {modalType === 'viewBooking' && selectedItem && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Name</p><p className="text-cream">{selectedItem.full_name}</p></div>
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Email</p><p className="text-cream">{selectedItem.email}</p></div>
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Phone</p><p className="text-cream">{selectedItem.mobile}</p></div>
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Location</p><p className="text-cream">{selectedItem.city}, {selectedItem.country}</p></div>
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Session</p><p className="text-gold">{selectedItem.session_type}</p></div>
                  <div><p className="text-cream-400 text-xs uppercase mb-1">Scheduled</p><p className="text-cream font-medium">{selectedItem.preferred_date} • {selectedItem.preferred_time}</p></div>
                </div>
                {selectedItem.alternative_date && <div><p className="text-cream-400 text-xs uppercase mb-1">Alternative</p><p className="text-cream-300">{selectedItem.alternative_date} • {selectedItem.alternative_time}</p></div>}
                {selectedItem.notes && <div><p className="text-cream-400 text-xs uppercase mb-1">Notes</p><p className="text-cream-300">{selectedItem.notes}</p></div>}
              </div>
            )}

            {/* User Forms */}
            {(modalType === 'createUser' || modalType === 'editUser') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Username</label><input type="text" value={formData.username || ''} onChange={(e) => setFormData({...formData, username: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>
                {modalType === 'createUser' && <div><label className="block text-cream-400 text-xs uppercase mb-2">Password</label><input type="password" value={formData.password || ''} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>}
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Email</label><input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Full Name</label><input type="text" value={formData.full_name || ''} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Role</label><select value={formData.role || 'editor'} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="viewer">Viewer</option><option value="editor">Editor</option><option value="manager">Manager</option><option value="super_admin">Super Admin</option></select></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createUser' ? 'Create User' : 'Save Changes'}</button>
              </form>
            )}

            {/* Program Forms - with File Upload and Full Metadata */}
            {(modalType === 'createProgram' || modalType === 'editProgram') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (English) *</label><input type="text" value={formData.title || ''} onChange={(e) => { const title = e.target.value; setFormData({...formData, title, slug: formData.slug || title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (Arabic)</label><input type="text" value={formData.arabic_title || ''} onChange={(e) => setFormData({...formData, arabic_title: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" dir="rtl" /></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">URL Slug *</label><input type="text" value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^\w-]/g, '-')})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream font-mono" placeholder="my-program" required /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Description *</label><textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-24" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Badge</label><select value={formData.badge || ''} onChange={(e) => setFormData({...formData, badge: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="">Select Badge...</option>{programBadges.map(b => <option key={b} value={b}>{b}</option>)}</select></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Format</label><select value={formData.format || ''} onChange={(e) => setFormData({...formData, format: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="">Select Format...</option>{programFormats.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
                </div>
                {/* Added: Date, Time, Price, Capacity fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Start Date</label><input type="date" value={formData.start_date || ''} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Start Time</label><input type="time" value={formData.start_time || ''} onChange={(e) => setFormData({...formData, start_time: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Price</label><input type="number" value={formData.price || ''} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="500" min="0" /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Capacity</label><input type="number" value={formData.capacity || ''} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || null})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="20" min="1" /></div>
                </div>
                {/* What You Work On */}
                <div className="border-t border-white/10 pt-4">
                  <p className="text-cream-400 text-xs uppercase mb-3">What You Work On</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-cream-400/70 text-xs mb-2">English Items</p>
                      {(formData.focus || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2">
                          <input type="text" value={item} onChange={(e) => { const f = [...(formData.focus || [])]; f[idx] = e.target.value; setFormData({...formData, focus: f}); }} className="flex-1 bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="e.g. Emotional awareness" />
                          <button type="button" onClick={() => { const f = (formData.focus || []).filter((_, i) => i !== idx); setFormData({...formData, focus: f}); }} className="text-red-400 hover:text-red-300 text-xl leading-none flex-shrink-0">×</button>
                        </div>
                      ))}
                      <button type="button" onClick={() => setFormData({...formData, focus: [...(formData.focus || []), '']})} className="text-gold text-xs hover:text-gold/80">+ Add Item</button>
                    </div>
                    <div>
                      <p className="text-cream-400/70 text-xs mb-2">Arabic Items (RTL)</p>
                      {(formData.focus_ar || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2">
                          <input type="text" dir="rtl" value={item} onChange={(e) => { const f = [...(formData.focus_ar || [])]; f[idx] = e.target.value; setFormData({...formData, focus_ar: f}); }} className="flex-1 bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="مثال: الوعي العاطفي" />
                          <button type="button" onClick={() => { const f = (formData.focus_ar || []).filter((_, i) => i !== idx); setFormData({...formData, focus_ar: f}); }} className="text-red-400 hover:text-red-300 text-xl leading-none flex-shrink-0">×</button>
                        </div>
                      ))}
                      <button type="button" onClick={() => setFormData({...formData, focus_ar: [...(formData.focus_ar || []), '']})} className="text-gold text-xs hover:text-gold/80">+ Add Item</button>
                    </div>
                  </div>
                </div>
                {/* Investment Details */}
                <div className="border-t border-white/10 pt-4">
                  <p className="text-cream-400 text-xs uppercase mb-3">Investment Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Primary Price (EN)</label>
                      <input type="text" value={(formData.investment || {}).monthly || ''} onChange={(e) => setFormData({...formData, investment: {...(formData.investment || {}), monthly: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="EGP 3,000 per month" />
                    </div>
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Primary Price (AR)</label>
                      <input type="text" dir="rtl" value={(formData.investment_ar || {}).monthly || ''} onChange={(e) => setFormData({...formData, investment_ar: {...(formData.investment_ar || {}), monthly: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="3,000 جنيه مصري شهرياً" />
                    </div>
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Full Payment (EN)</label>
                      <input type="text" value={(formData.investment || {}).full || ''} onChange={(e) => setFormData({...formData, investment: {...(formData.investment || {}), full: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="EGP 9,000 — full program payment" />
                    </div>
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Full Payment (AR)</label>
                      <input type="text" dir="rtl" value={(formData.investment_ar || {}).full || ''} onChange={(e) => setFormData({...formData, investment_ar: {...(formData.investment_ar || {}), full: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="9,000 جنيه مصري — دفعة كاملة" />
                    </div>
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Prepaid Discount (EN)</label>
                      <input type="text" value={(formData.investment || {}).prepaid || ''} onChange={(e) => setFormData({...formData, investment: {...(formData.investment || {}), prepaid: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="EGP 8,000 — prepaid discount" />
                    </div>
                    <div>
                      <label className="block text-cream-400/70 text-xs mb-1">Prepaid Discount (AR)</label>
                      <input type="text" dir="rtl" value={(formData.investment_ar || {}).prepaid || ''} onChange={(e) => setFormData({...formData, investment_ar: {...(formData.investment_ar || {}), prepaid: e.target.value}})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm" placeholder="8,000 جنيه مصري — خصم الدفع المسبق" />
                    </div>
                  </div>
                </div>
                <StableFileUpload label="Program Image" currentUrl={formData.image_url} onUpload={(url) => setFormData({...formData, image_url: url})} folder="programs" />
                <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4" /><label className="text-cream-300">Active</label></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createProgram' ? 'Create Program' : 'Save Changes'}</button>
              </form>
            )}

            {/* Session Forms - with File Upload */}
            {(modalType === 'createSession' || modalType === 'editSession') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (English) *</label><input type="text" value={formData.title_en || formData.title || ''} onChange={(e) => { const title = e.target.value; setFormData({...formData, title_en: title, title: title, slug: formData.slug || title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (Arabic)</label><input type="text" value={formData.title_ar || formData.arabic_title || ''} onChange={(e) => setFormData({...formData, title_ar: e.target.value, arabic_title: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" dir="rtl" /></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">URL Slug *</label><input type="text" value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^\w-]/g, '-')})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream font-mono" placeholder="my-session" required /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Description *</label><textarea value={formData.description_en || formData.description || ''} onChange={(e) => setFormData({...formData, description_en: e.target.value, description: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-24" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Session Type *</label><select value={formData.session_type || 'crystal-healing'} onChange={(e) => setFormData({...formData, session_type: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required>{sessionTypes.map(t => <option key={t} value={t}>{t.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}</select></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Duration (minutes)</label><input type="number" value={formData.duration_minutes || 60} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 60})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Price</label><input type="text" value={formData.price || ''} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="500" /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Currency</label><select value={formData.currency || 'EGP'} onChange={(e) => setFormData({...formData, currency: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="EGP">EGP</option><option value="USD">USD</option><option value="EUR">EUR</option></select></div>
                </div>
                <StableFileUpload label="Session Image" currentUrl={formData.image_url} onUpload={(url) => setFormData({...formData, image_url: url})} folder="sessions" />
                <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4" /><label className="text-cream-300">Active</label></div>
                {/* Featured toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gold/20 bg-gold/5">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, is_featured: !formData.is_featured})}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${formData.is_featured ? 'bg-gold' : 'bg-white/20'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${formData.is_featured ? 'left-6' : 'left-1'}`} />
                  </button>
                  <label className="text-cream-300 text-sm cursor-pointer select-none" onClick={() => setFormData({...formData, is_featured: !formData.is_featured})}>
                    <span className="text-gold font-medium">Set as Featured Session</span>
                    <span className="block text-cream-400 text-xs mt-0.5">The featured session appears as the hero on the public page</span>
                  </label>
                </div>
                {/* Expectations dynamic list */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase mb-2 tracking-wider">What You Can Expect</label>
                  <div className="space-y-2">
                    {(formData.expectations || []).map((item, idx) => (
                      <div key={idx} className="flex gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => {
                              const updated = [...(formData.expectations || [])];
                              [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                              setFormData({...formData, expectations: updated});
                            }}
                            className="px-1.5 py-0.5 text-cream-400 hover:text-gold disabled:opacity-20 disabled:cursor-not-allowed text-xs leading-none"
                          >▲</button>
                          <button
                            type="button"
                            disabled={idx === (formData.expectations || []).length - 1}
                            onClick={() => {
                              const updated = [...(formData.expectations || [])];
                              [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                              setFormData({...formData, expectations: updated});
                            }}
                            className="px-1.5 py-0.5 text-cream-400 hover:text-gold disabled:opacity-20 disabled:cursor-not-allowed text-xs leading-none"
                          >▼</button>
                        </div>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const updated = [...(formData.expectations || [])];
                            updated[idx] = e.target.value;
                            setFormData({...formData, expectations: updated});
                          }}
                          placeholder={`Expectation ${idx + 1}`}
                          className="flex-1 bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (formData.expectations || []).filter((_, i) => i !== idx);
                            setFormData({...formData, expectations: updated});
                          }}
                          className="px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors text-sm"
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, expectations: [...(formData.expectations || []), '']})}
                      className="text-gold text-xs hover:text-gold/80 flex items-center gap-1 mt-1"
                    >+ Add Item</button>
                  </div>
                </div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createSession' ? 'Create Session' : 'Save Changes'}</button>
              </form>
            )}

            {/* Media Forms */}
            {(modalType === 'createMedia' || modalType === 'editMedia') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <StableFileUpload label="Upload File" currentUrl={formData.url} onUpload={(url) => { const filename = url.split('/').pop(); const ext = filename.split('.').pop()?.toLowerCase(); const file_type = ['jpg','jpeg','png','gif','webp','svg'].includes(ext) ? 'image' : ['mp4','mov','webm'].includes(ext) ? 'video' : 'document'; setFormData({...formData, url, filename: filename, file_type, name: formData.name || filename}); }} folder={formData.folder || 'general'} />
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Display Name *</label><input type="text" value={formData.name || formData.filename || ''} onChange={(e) => setFormData({...formData, name: e.target.value, filename: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Folder</label><select value={formData.folder || 'general'} onChange={(e) => setFormData({...formData, folder: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream">{mediaFolders.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}</select></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Alt Text</label><input type="text" value={formData.alt_text || ''} onChange={(e) => setFormData({...formData, alt_text: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="Description for accessibility" /></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createMedia' ? 'Add Media' : 'Save Changes'}</button>
              </form>
            )}

            {/* View Media */}
            {modalType === 'viewMedia' && selectedItem && (
              <div className="space-y-4">
                <img src={selectedItem.url} alt={selectedItem.name} className="w-full rounded-lg" />
                <div><p className="text-cream-400 text-xs uppercase mb-1">Name</p><p className="text-cream">{selectedItem.name || selectedItem.filename}</p></div>
                <div><p className="text-cream-400 text-xs uppercase mb-1">URL</p><p className="text-cream-300 text-sm break-all">{selectedItem.url}</p></div>
                <button
                  onClick={() => downloadMedia(selectedItem)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-charcoal rounded-lg font-medium hover:bg-gold-500 transition-colors"
                >
                  <Download size={16} /> Download File
                </button>
              </div>
            )}

            {/* Page Forms - with WYSIWYG Editor */}
            {(modalType === 'createPage' || modalType === 'editPage') && (
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                onClickCapture={(e) => {
                  if (e.target.closest?.('.ql-toolbar')) e.preventDefault();
                }}
              >
                <div><label className="block text-cream-400 text-xs uppercase mb-2">URL Slug *</label><input type="text" value={formData.slug || ''} onChange={(e) => { const v = e.target.value.toLowerCase().replace(/[^\w-]/g, '-'); setFormData(prev => ({...prev, slug: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream font-mono" placeholder="about-us" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (English) *</label><input type="text" value={getTextContent(formData.title_en) || getTextContent(formData.title) || ''} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, title_en: v, title: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (Arabic)</label><input type="text" value={formData.title_ar || ''} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, title_ar: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" dir="rtl" /></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Content (English)</label>
                  <div className="bg-white rounded-lg">
                    <ReactQuill
                      key={`page-en-${selectedItem?.id || 'new'}`}
                      ref={quillRefPageEN}
                      theme="snow"
                      value={formData.content_en || formData.content || ''}
                      onChange={(value) => setFormData(prev => ({...prev, content_en: value}))}
                      className="bg-white text-charcoal rounded-lg"
                      modules={quillModulesPageEN}
                      formats={QUILL_FORMATS}
                    />
                  </div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Content (Arabic)</label>
                  <div className="bg-white rounded-lg">
                    <ReactQuill
                      key={`page-ar-${selectedItem?.id || 'new'}`}
                      ref={quillRefPageAR}
                      theme="snow"
                      value={formData.content_ar || ''}
                      onChange={(value) => setFormData(prev => ({...prev, content_ar: value}))}
                      className="bg-white text-charcoal rounded-lg"
                      dir="rtl"
                      modules={quillModulesPageAR}
                      formats={QUILL_FORMATS}
                    />
                  </div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Template</label><select value={formData.template || 'default'} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, template: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="default">Default</option><option value="landing">Landing</option><option value="book">Book</option><option value="program">Program</option></select></div>
                <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_published !== false} onChange={(e) => { const v = e.target.checked; setFormData(prev => ({...prev, is_published: v})); }} className="w-4 h-4" /><label className="text-cream-300">Published</label></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createPage' ? 'Create Page' : 'Save Changes'}</button>
              </form>
            )}

            {/* Section Forms - with WYSIWYG Editor */}
            {(modalType === 'createSection' || modalType === 'editSection') && (
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                onClickCapture={(e) => {
                  if (e.target.closest?.('.ql-toolbar')) e.preventDefault();
                }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Page *</label><select value={formData.page_id || ''} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, page_id: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required><option value="">Select Page...</option>{pagesList.map(p => <option key={p.id} value={p.id}>{p.title || p.slug}</option>)}</select></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Section Type *</label><select value={formData.section_type || 'custom'} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, section_type: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required>{sectionTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, ' ')}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (English)</label><input type="text" value={getTextContent(formData.title_en) || getTextContent(formData.title) || ''} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, title_en: v, title: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Title (Arabic)</label><input type="text" value={formData.title_ar || ''} onChange={(e) => { const v = e.target.value; setFormData(prev => ({...prev, title_ar: v})); }} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" dir="rtl" /></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Content (English)</label>
                  <div className="bg-white rounded-lg">
                    <ReactQuill
                      key={`section-en-${selectedItem?.id || 'new'}`}
                      ref={quillRefSectionEN}
                      theme="snow"
                      value={formData.content_en || formData.content || ''}
                      onChange={(value) => setFormData(prev => ({...prev, content_en: value}))}
                      className="bg-white text-charcoal rounded-lg"
                      modules={quillModulesSectionEN}
                      formats={QUILL_FORMATS}
                    />
                  </div>
                </div>
                <StableFileUpload label="Section Image" currentUrl={formData.image_url} onUpload={(url) => setFormData(prev => ({...prev, image_url: url}))} folder="sections" />
                <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_visible !== false} onChange={(e) => { const v = e.target.checked; setFormData(prev => ({...prev, is_visible: v})); }} className="w-4 h-4" /><label className="text-cream-300">Visible</label></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createSection' ? 'Create Section' : 'Save Changes'}</button>
              </form>
            )}

            {/* Review Edit Form - with program reassignment */}
            {modalType === 'editReview' && selectedItem && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Reviewer Name</label><input type="text" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Rating</label><select value={formData.rating || 5} onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream">{[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Star{r !== 1 ? 's' : ''}</option>)}</select></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Title</label><input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Content</label><textarea value={formData.content || ''} onChange={(e) => setFormData({...formData, content: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-32" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Assign to Program</label><select value={formData.program_id || ''} onChange={(e) => setFormData({...formData, program_id: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="">No Program</option>{programsList.map(p => <option key={p.id} value={p.id}>{p.title || p.slug}</option>)}</select></div>
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Display Program Name</label><input type="text" value={formData.program_name || ''} onChange={(e) => setFormData({...formData, program_name: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" placeholder="Override program name" /></div>
                </div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Status</label><select value={formData.status || 'pending'} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream"><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="featured">Featured</option></select></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Admin Notes</label><textarea value={formData.admin_notes || ''} onChange={(e) => setFormData({...formData, admin_notes: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-20" placeholder="Internal notes..." /></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">Save Changes</button>
              </form>
            )}

            {/* Digital Asset Forms */}
            {(modalType === 'createDigitalAsset' || modalType === 'editDigitalAsset') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Title</label><input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" required placeholder="Filter Challenge - Week 1" /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Description</label><textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream h-24" placeholder="Description for download page..." /></div>
                <div><label className="block text-cream-400 text-xs uppercase mb-2">Challenge Name</label><input type="text" value={formData.challenge_name || 'Filter Challenge'} onChange={(e) => setFormData({...formData, challenge_name: e.target.value})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream" /></div>
                <StableFileUpload label="Upload File" currentUrl={formData.file_url} onUpload={(url) => setFormData({...formData, file_url: url})} folder="digital-assets" />
                <div className="flex items-center gap-3 p-3 bg-charcoal border border-white/10 rounded-lg">
                  <input type="checkbox" id="requires_code_toggle" checked={!!formData.requires_code} onChange={(e) => setFormData({...formData, requires_code: e.target.checked})} className="w-4 h-4 accent-gold" />
                  <div>
                    <label htmlFor="requires_code_toggle" className="text-cream-300 text-sm cursor-pointer">Require access code to download</label>
                    <p className="text-cream-500 text-xs mt-0.5">Leave unchecked for a free public download with no gate</p>
                  </div>
                </div>
                {formData.requires_code && (
                  <div><label className="block text-cream-400 text-xs uppercase mb-2">Access Code</label><input type="text" value={formData.access_code || ''} onChange={(e) => setFormData({...formData, access_code: e.target.value.toUpperCase()})} className="w-full bg-charcoal-100 border border-white/10 rounded-lg px-4 py-3 text-cream font-mono" placeholder="e.g. HEALING1 (auto-generated if left blank)" maxLength={12} /></div>
                )}
                <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4" /><label className="text-cream-300">Active</label></div>
                <button type="submit" className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500">{modalType === 'createDigitalAsset' ? 'Create Asset' : 'Save Changes'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
  ) : null;

  return (
    <div className="min-h-screen bg-charcoal-500">
      {/* Header */}
      <header className="bg-charcoal border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-cream-400 hover:text-gold">{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
            <div className="flex items-center gap-3"><Shield size={24} className="text-gold" /><span className="font-script text-xl text-gold hidden sm:block">Admin Dashboard</span></div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 px-3 py-2 bg-charcoal-100 border border-white/10 rounded-lg text-cream-300 hover:text-gold text-sm"><Home size={16} /><span className="hidden sm:inline">Back to Website</span></Link>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right"><p className="text-cream text-sm font-medium">{user?.full_name || user?.username}</p><p className="text-cream-400 text-xs">{user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</p></div>
              <button onClick={logout} className="p-2 text-cream-400 hover:text-red-400" title="Logout"><LogOut size={20} /></button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 bg-charcoal border-r border-white/10 min-h-[calc(100vh-64px)] sticky top-16">
          <nav className="p-4 space-y-2">
            {filteredTabs.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setStatusFilter(''); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id ? 'bg-gold/20 text-gold' : 'text-cream-300 hover:bg-white/5 hover:text-cream'}`}>
                <tab.icon size={18} /><span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-30">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <aside className="absolute left-0 top-16 bottom-0 w-64 bg-charcoal border-r border-white/10 overflow-y-auto">
              <nav className="p-4 space-y-2">
                {filteredTabs.map(tab => (
                  <button key={tab.id} onClick={() => { setActiveTab(tab.id); setStatusFilter(''); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id ? 'bg-gold/20 text-gold' : 'text-cream-300 hover:bg-white/5 hover:text-cream'}`}>
                    <tab.icon size={18} /><span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {isLoading ? (
            <div className="flex items-center justify-center h-64"><RefreshCw size={32} className="text-gold animate-spin" /></div>
          ) : (
            <>
              {activeTab === 'dashboard' && <DashboardView />}
              {activeTab === 'bookings' && <BookingsView />}
              {activeTab === 'contacts' && <ContactsView />}
              {activeTab === 'reviews' && isSuperAdmin() && <ReviewsView />}
              {activeTab === 'programs' && isSuperAdmin() && <ProgramsView />}
              {activeTab === 'sessions' && isSuperAdmin() && <SessionsView />}
              {activeTab === 'pages' && isSuperAdmin() && <PagesView />}
              {activeTab === 'sections' && isSuperAdmin() && <SectionsView />}
              {activeTab === 'media' && isSuperAdmin() && <MediaView />}
              {activeTab === 'digital-assets' && isSuperAdmin() && <DigitalAssetsView />}
              {activeTab === 'challenges' && isSuperAdmin() && (
                <div>
                  <h2 className="font-serif text-2xl text-cream mb-6">Challenge Manager</h2>
                  <AdminChallengeManager getAuthHeader={getAuthHeader} />
                </div>
              )}
              {activeTab === 'gallery' && isSuperAdmin() && <GalleryView galleryItems={galleryItems} fetchGalleryItems={fetchGalleryItems} backendUrl={backendUrl} getAuthHeader={getAuthHeader} uploadFile={uploadFile} />}
              {activeTab === 'about-editor' && isSuperAdmin() && <AboutEditorView aboutForm={aboutForm} setAboutForm={setAboutForm} aboutSaving={aboutSaving} setAboutSaving={setAboutSaving} setAboutData={setAboutData} uploadFile={uploadFile} backendUrl={backendUrl} getAuthHeader={getAuthHeader} />}
              {activeTab === 'settings' && isSuperAdmin() && <SettingsView />}
              {activeTab === 'users' && isSuperAdmin() && <UsersView />}
            </>
          )}
        </main>
      </div>

      {modalContent}
    </div>
  );
};

export default AdminDashboard;
